import { useSearchParams } from 'react-router'
import { Invoice } from 'types'
import type { TableProps } from 'antd'
import { buildDateFilter } from '../translators/buildDateFilter'
import { FilterConfig, UseInvoiceListFiltersResult } from '../types'
import { QUERY_PARAMS } from '../constants'

/**
 * Returns a handler that resets the page to 1 and sets or deletes a single
 * search-param key based on whether `value` is non-empty.
 */
const createSearchParamHandler =
  (key: string, setter: ReturnType<typeof useSearchParams>[1]) =>
  (value: string): void => {
    setter(
      (prev) => {
        prev.set(QUERY_PARAMS.PAGE, '1')
        if (value) { prev.set(key, value) } else { prev.delete(key) }
        return prev
      },
      { replace: true }
    )
  }

/**
 * Returns a handler that resets the page to 1 and sets or deletes a pair of
 * search-param keys for a date range (`fromKey` / `toKey`).
 */
const createRangeParamHandler =
  (fromKey: string, toKey: string, setter: ReturnType<typeof useSearchParams>[1]) =>
  (from?: string, to?: string): void => {
    setter((prev) => {
      prev.set(QUERY_PARAMS.PAGE, '1')
      if (from) { prev.set(fromKey, from) } else { prev.delete(fromKey) }
      if (to) { prev.set(toKey, to) } else { prev.delete(toKey) }
      return prev
    })
  }

// All filter state lives in the URL so the list view is fully shareable and survives a page reload.
const useInvoiceListFilters = (): UseInvoiceListFiltersResult => {
  const [searchParams, setSearchParams] = useSearchParams()

  const currentPage = Number(searchParams.get(QUERY_PARAMS.PAGE)) || 1
  const searchId = searchParams.get(QUERY_PARAMS.SEARCH_ID) ?? ''
  const searchCustomer = searchParams.get(QUERY_PARAMS.SEARCH_CUSTOMER) ?? ''
  const searchAddress = searchParams.get(QUERY_PARAMS.SEARCH_ADDRESS) ?? ''
  const statusParam = searchParams.get(QUERY_PARAMS.STATUS) ?? ''
  const statusFilter = statusParam ? statusParam.split(',') : []
  const sort = searchParams.get(QUERY_PARAMS.SORT) ?? undefined
  const dateFrom = searchParams.get(QUERY_PARAMS.DATE_FROM) ?? undefined
  const dateTo = searchParams.get(QUERY_PARAMS.DATE_TO) ?? undefined
  const deadlineFrom = searchParams.get(QUERY_PARAMS.DEADLINE_FROM) ?? undefined
  const deadlineTo = searchParams.get(QUERY_PARAMS.DEADLINE_TO) ?? undefined
  const dateFilter = buildDateFilter(dateFrom, dateTo, deadlineFrom, deadlineTo)

  const handleIdSearchChange = createSearchParamHandler(QUERY_PARAMS.SEARCH_ID, setSearchParams)
  const handleCustomerSearchChange = createSearchParamHandler(QUERY_PARAMS.SEARCH_CUSTOMER, setSearchParams)
  const handleAddressSearchChange = createSearchParamHandler(QUERY_PARAMS.SEARCH_ADDRESS, setSearchParams)

  const handleStatusFilterChange = (statuses: string[]) => {
    setSearchParams((prev) => {
      prev.set(QUERY_PARAMS.PAGE, '1')
      if (statuses.length) { prev.set(QUERY_PARAMS.STATUS, statuses.join(',')) } else { prev.delete(QUERY_PARAMS.STATUS) }
      return prev
    })
  }

  const handleDateRangeChange = createRangeParamHandler(QUERY_PARAMS.DATE_FROM, QUERY_PARAMS.DATE_TO, setSearchParams)
  const handleDeadlineRangeChange = createRangeParamHandler(QUERY_PARAMS.DEADLINE_FROM, QUERY_PARAMS.DEADLINE_TO, setSearchParams)

  const handleTableChange: TableProps<Invoice>['onChange'] = (
    pagination,
    __,
    sorter
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] ?? {} : sorter
    setSearchParams((prev) => {
      if (pagination.current) {
        prev.set(QUERY_PARAMS.PAGE, pagination.current.toString())
      }
      if (s.order && s.columnKey) {
        const prefix = s.order === 'ascend' ? '+' : '-'
        prev.set(QUERY_PARAMS.SORT, `${prefix}${s.columnKey}`)
      } else {
        prev.delete(QUERY_PARAMS.SORT)
      }
      return prev
    })
  }

  const filterConfig: FilterConfig = {
    sort,
    searchId,
    searchCustomer,
    searchAddress,
    statusFilter,
    dateFrom,
    dateTo,
    deadlineFrom,
    deadlineTo,
    onIdSearchChange: handleIdSearchChange,
    onCustomerSearchChange: handleCustomerSearchChange,
    onAddressSearchChange: handleAddressSearchChange,
    onStatusFilterChange: handleStatusFilterChange,
    onDateRangeChange: handleDateRangeChange,
    onDeadlineRangeChange: handleDeadlineRangeChange,
  }

  return {
    filters: {
      currentPage,
      searchId,
      searchCustomer,
      searchAddress,
      statusFilter,
      sort,
      dateFrom,
      dateTo,
      deadlineFrom,
      deadlineTo,
      dateFilter,
    },
    filterConfig,
    handleTableChange,
  }
}

export default useInvoiceListFilters
